import { useLayoutEffect, useRef } from 'react';
import { springs } from './engine';
import { transition, exitGhost } from './effects';

// Enter and exit animation for an element that appears and disappears with its component.
//
//   const ref = useRef();
//   useMotion(ref, { enter: { y: 20, opacity: 0 }, exit: { opacity: 0 } });
//   return <div ref={ref}>…</div>;
//
// enter   state the element springs in from when it mounts
// exit    state it springs to when it unmounts (played on a copy, React has removed the real one)
// parts   { selector: { enter, exit, origin } } children with their own states, same spring
// origin  transform-origin
// config / exitConfig  springs for enter and exit
// ghost   where the exit copy goes: { parent, below, zIndex } (default: on top, in <body>)
// force   play the exit even while exits are suppressed (page changes)
// exit and ghost may be functions, evaluated when the exit starts.
export function useMotion(ref, options) {
  const opts = useRef(options);
  opts.current = options;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const { enter, parts = {}, origin, config = springs.snappy } = opts.current;
    const targets = [];
    if (enter) targets.push({ el, from: enter, origin });
    for (const [selector, part] of Object.entries(parts)) {
      if (part.enter) targets.push({ el: el.querySelector(selector), from: part.enter, origin: part.origin });
    }
    const enterCtrl = targets.length ? transition(targets, { config }) : null;

    return () => {
      enterCtrl?.stop();
      const o = opts.current;
      const value = (v) => (typeof v === 'function' ? v() : v);
      const exit = value(o.exit);
      const partExits = {};
      for (const [selector, part] of Object.entries(o.parts || {})) if (part.exit) partExits[selector] = part.exit;
      if (!exit && !Object.keys(partExits).length) return;
      exitGhost(el, { to: exit, parts: partExits, config: o.exitConfig || springs.exit, force: o.force, ...value(o.ghost) });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
